import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getGraphQLClient, GET_BOOK_CHAPTERS_TREE } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Spinner } from '../components/common';

type BookChaptersResponse = {
  book: {
    id: string;
    chapters: { id: string }[];
  } | null;
};

/**
 * /books/:bookId is now a redirect to the chapters-first workspace.
 * If the book has no chapters yet, send the user into chapter-flow selection;
 * otherwise open the refinement workspace.
 */
export default function BookPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!bookId) {
      navigate('/books', { replace: true });
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const client = getGraphQLClient();
        const data = await client.request<BookChaptersResponse>(GET_BOOK_CHAPTERS_TREE, {
          id: bookId,
        });
        if (cancelled) return;
        if (!data.book) {
          navigate('/books', { replace: true });
          return;
        }
        const hasChapters = (data.book.chapters?.length ?? 0) > 0;
        navigate(hasChapters ? `/books/${bookId}/refine` : `/books/${bookId}/chapters-first/select`, {
          replace: true,
        });
      } catch (err) {
        captureException(err);
        if (!cancelled) navigate('/books', { replace: true });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [bookId, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
      <Spinner />
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('common.loading', 'Loading...')}
      </p>
    </div>
  );
}
